"use client";

import { Reveal } from "@/components/ui/Reveal";

export type ServiceGridItem = {
  title: string;
  description: string;
  iconSrc?: string;
  iconAlt?: string;
  featured?: boolean;
};

export type ServicesGridSectionProps = {
  eyebrow?: string;
  heading?: string;
  description?: string;
  services?: ServiceGridItem[];
  className?: string;
};

const defaultServices: ServiceGridItem[] = [
  {
    title: "Target Audience Analysis",
    description:
      "Understanding your readers is key. We study demographics, interests, and reading habits to identify the perfect audience. This insight allows us to create campaigns that truly resonate and convert readers into loyal fans.",
    iconSrc: "/landing/marketing/icon-1.webp",
    iconAlt: "Target Audience Analysis",
  },
  {
    title: "Strategic Campaign Planning",
    description:
      "Clear strategy drives effective results. We plan structured campaigns across blogs, newsletters, social media, and multimedia to build engagement and strengthen your author presence. Every step is designed to create consistent visibility and lasting impact.",
    iconSrc: "/landing/marketing/icon-2.webp",
    iconAlt: "Strategic Campaign Planning",
    featured: true,
  },
  {
    title: "Content Marketing",
    description:
      "Engaging content drives reader interest and builds your author brand. Through blog posts, social media updates, newsletters, and multimedia campaigns, our book marketing services create meaningful connections with your audience. Compelling content encourages sharing, boosts visibility, and positions your work as a must-read in its genre.",
    iconSrc: "/landing/marketing/icon-3.webp",
    iconAlt: "Content Marketing",
  },
  {
    title: "Book Launch Promotion",
    description:
      "A strong launch sets the tone for your book’s success. We create buzz through pre-launch campaigns, social media teasers, and targeted outreach. With our book marketing services for self-published authors, your launch attracts attention, generates reviews, and engages readers from day one.",
    iconSrc: "/landing/marketing/icon-4.webp",
    iconAlt: "Book Launch Promotion",
  },
  {
    title: "Online Presence Building",
    description:
      "A professional online footprint is essential. Hence, when it comes to building an online presence for authors, we strive for maximum channels and mediums relevant to your genre and audience. Online presence building includes optimizing websites, creating author profiles, and listings to make you and your book easy to find and highly visible.",
    iconSrc: "/landing/marketing/icon-5.webp",
    iconAlt: "Online Presence Building",
    featured: true,
  },
  {
    title: "Reviews and Influencer Outreach",
    description:
      "Positive reviews and trusted influencer endorsements strengthen your book’s credibility and expand its reach. We identify relevant reviewers, bloggers, and social media influencers, #BookTok community, then manage outreach and campaign coordination to showcase your work effectively. Our affordable book marketing services help your book gain momentum and connect with wider, highly targeted audiences.",
    iconSrc: "/landing/marketing/icon-6.webp",
    iconAlt: "Reviews and Influencer Outreach",
  },
];

export function ServicesGridSection({
  eyebrow,
  heading = "Effective Book Marketing Services To Boost Visibility",
  description = "At United Book Publishing, we help authors connect with the right readers, grow sales, and expand their audience. Our book marketing services combine strategy, creativity, and practical execution to make your book stand out.",
  services = defaultServices,
  className,
}: ServicesGridSectionProps) {
  return (
    <section
      id="services"
      className={["bg-[#f5f5f5] py-16 lg:py-24", className]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          {eyebrow ? (
            <Reveal>
              <p className="font-heading text-sm font-semibold uppercase tracking-[0.15em] text-[#b46c29]">
                {eyebrow}
              </p>
            </Reveal>
          ) : null}
          <Reveal delayMs={eyebrow ? 80 : 0}>
            <h2 className="font-heading text-3xl font-bold text-[#b46c29] sm:text-4xl">
              {heading}
            </h2>
          </Reveal>
          {description ? (
            <Reveal delayMs={160}>
              <p className="mt-4 text-base leading-relaxed text-gray-600">
                {description}
              </p>
            </Reveal>
          ) : null}
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {services.map((service, index) => (
            <Reveal key={service.title} delayMs={index * 70} className="h-full">
              <article
                className={[
                  "market-box group relative flex h-full flex-col items-center rounded-[12px] bg-[#eaeaea] p-6 text-center text-[#29361a] sm:items-start sm:p-7 sm:text-left",
                  service.featured ? "ring-1 ring-[#29361a]/15" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {service.iconSrc ? (
                  <img
                    src={service.iconSrc}
                    alt={service.iconAlt ?? service.title}
                    loading="lazy"
                    decoding="async"
                    className="mb-4 h-12 w-12 object-contain transition duration-300"
                  />
                ) : null}
                <h3 className="relative z-1 font-heading text-xl font-bold text-[#b46c29] sm:text-2xl">
                  {service.title}
                </h3>
                <p className="relative z-1 mt-3 text-sm leading-relaxed text-gray-700 sm:text-base">
                  {service.description}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
